import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';

import { AuthenticationService } from 'src/app/modules/authentication/authentication.service';
import { ToastNotificationService } from 'src/app/shared/services/toast-notification/toast-notification.service';
import { Router } from '@angular/router';
import { USER_KEY } from '../auth/constants';
import { FeatureFlagService } from 'src/app/shared/services/feature-flags/feature-flag.service';

@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  private isRefreshing: boolean = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null
  );
  constructor(
    private authService: AuthenticationService,
    private toastNotificationService: ToastNotificationService,
    private RouterService: Router,
    private featureFlagService: FeatureFlagService
  ) {}

  public intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<Object>> {
    let authReq = req;
    const token = this.authService.token;
    if (token) authReq = this.addTokenHeader(req, token);
    return next.handle(authReq).pipe(
      catchError((error) => {
      const refreshToken = this.authService.refreshToken;
      if (
          error instanceof HttpErrorResponse &&
          !authReq.url.includes('/login') &&
          refreshToken &&
          error.status === 401
        ) {
          return this.handle401Error(authReq, next);
        }
        return throwError(error);
      })
    );
  }
  /**
   * @description Refresh token if expired
   * @param request
   * @param next
   * @returns Observable
   */
  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);
      const refreshToken = this.authService.refreshToken;
      if (refreshToken) {
        return this.authService.tokenRefresh().pipe(
          switchMap((token: any) => {
            this.isRefreshing = false;
            this.authService.updateToken(token);
            // Self-heal flags that never loaded (e.g. the initial fetch failed
            // before the first 401) without disturbing an already-loaded,
            // non-stale cache — ensureLoaded() is a no-op in that case.
            this.featureFlagService.ensureLoaded().subscribe();
            this.refreshTokenSubject.next(token.access_token);
            return next.handle(
              this.addTokenHeader(request, token.access_token)
            );
          }),
          catchError((error: any) => {
            this.isRefreshing = false;
            this.toastNotificationService.open({
              message: 'Your session has expired',
            });
            sessionStorage.removeItem(USER_KEY);
            this.RouterService.navigate(['/login']);
            return throwError(error);
          })
        );
      }
    }
    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => next.handle(this.addTokenHeader(request, token)))
    );
  }
  /**
   * @description Set Authorization header with token
   * @param request
   * @param token
   * @returns Authorization header
   */
  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });
  }
}
